import React, { useCallback, useMemo, useRef } from "react";
import { DropDownBox, List } from "devextreme-react";
import DataSource from "devextreme/data/data_source";
import ArrayStore from "devextreme/data/array_store";
import { axiosGet } from "../../../apis/useAxios";
import useLookup from "../../../hooks/useLookup";

/**
 * Staff list for select boxes.
 * activeOnly=false -> GET Crew/get-crew-dropdown (every crew, active and inactive)
 * activeOnly=true  -> GET Crew/get-crew-dropdown-active
 * Each item: employeeNumber, employeeName, employeeFullName ("no - name"), designation (position), isActive.
 */
export async function getStaffList(activeOnly = false) {
  const airlineCode = sessionStorage.getItem("session_airlineCode");
  const url = activeOnly ? "Crew/get-crew-dropdown-active" : "Crew/get-crew-dropdown";
  const response = await axiosGet(url, { airlineCode });
  if (response && response.isSuccess === false && !/no record/i.test(response.message ?? "")) {
    throw new Error(response.message || "Unable to load staff.");
  }
  return response?.payload ?? [];
}

const el = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};

/**
 * One list row as plain DOM: name, then employee # · position chip · status pill.
 * Plain DOM (not a React template) keeps scrolling through thousands of staff fast.
 */
export function staffItemTemplate(item) {
  const row = el("div", "mst-staff-item");
  if (!item) return row;
  row.appendChild(el("div", "mst-staff-item__name", item.employeeName?.trim() || item.employeeNumber));

  const meta = el("div", "mst-staff-item__meta");
  const no = el("span", "mst-staff-item__no");
  const icon = el("i", "fas fa-id-badge");
  icon.setAttribute("aria-hidden", "true");
  no.append(icon, ` ${item.employeeNumber}`);
  meta.appendChild(no);
  if (item.designation) meta.appendChild(el("span", "mst-staff-item__position", item.designation));
  meta.appendChild(
    el("span", `mst-status ${item.isActive ? "is-active" : "is-inactive"}`, item.isActive ? "Active" : "Inactive"),
  );
  row.appendChild(meta);
  return row;
}

const SEARCH_EXPR = ["employeeNumber", "employeeName", "employeeFullName", "designation"];
const DROP_DOWN_OPTIONS = { minWidth: 300 };
const PAGE_SIZE = 20;

/**
 * Staff picker: a DropDownBox with a searchable, paged List inside (same pattern as Crew Activity "Staff ID").
 * The list has its own search box; clicking a row selects it and closes the drop-down.
 * value / onChange work with the employee number; onChange(employeeNumber, item) ("" and null when cleared).
 * The staff list is loaded once per session per airline (useLookup).
 */
export default function StaffSelectBox({
  value,
  onChange,
  activeOnly = false,
  placeholder = "Select Staff",
  readOnly = false,
  showClearButton = true,
  className = "",
  ...rest
}) {
  const airlineCode = sessionStorage.getItem("session_airlineCode");
  const { items, loading } = useLookup(
    `staff:${activeOnly ? "active" : "all"}:${airlineCode}`,
    () => getStaffList(activeOnly),
  );
  const refBox = useRef(null);
  const refList = useRef(null);

  // Keep the latest props for the stable handlers below.
  const latest = useRef({ items, onChange });
  latest.current = { items, onChange };

  // Paged list: renders 20 rows and loads more on scroll; search runs over the whole list.
  const listSource = useMemo(
    () =>
      new DataSource({
        store: new ArrayStore({ key: "employeeNumber", data: items }),
        sort: "employeeName",
        paginate: true,
        pageSize: PAGE_SIZE,
      }),
    [items],
  );

  const notify = (employeeNumber) => {
    const { items: list, onChange: handler } = latest.current;
    if (!handler) return;
    handler(employeeNumber ?? "", list.find((s) => s.employeeNumber === employeeNumber) ?? null);
  };

  const handleItemClick = useCallback((e) => {
    notify(e.itemData?.employeeNumber);
    if (refBox.current) refBox.current.instance.close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // DropDownBox value only changes from here via the clear button.
  const handleValueChanged = useCallback((e) => {
    if (e.event && (e.value === null || e.value === undefined)) notify("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fresh search each time it opens, with the cursor in the search box.
  const handleOpened = useCallback(() => {
    const list = refList.current?.instance;
    if (!list) return;
    list.option("searchValue", "");
    const search = list.element().querySelector(".dx-list-search input");
    if (search) search.focus();
  }, []);

  const renderList = useCallback(
    () => (
      <List
        ref={refList}
        dataSource={listSource}
        height={360}
        keyExpr="employeeNumber"
        displayExpr="employeeFullName"
        itemTemplate={staffItemTemplate}
        searchEnabled
        searchExpr={SEARCH_EXPR}
        searchMode="contains"
        pageLoadMode="scrollBottom"
        selectionMode="single"
        selectedItemKeys={value ? [value] : []}
        noDataText={loading ? "Loading staff..." : "No staff found"}
        onItemClick={handleItemClick}
      />
    ),
    [listSource, value, loading, handleItemClick],
  );

  return (
    <DropDownBox
      ref={refBox}
      className={`mst-staff-select ${className}`.trim()}
      dataSource={items}
      value={value || null}
      valueExpr="employeeNumber"
      displayExpr="employeeFullName"
      placeholder={loading ? "Loading staff..." : placeholder}
      showClearButton={showClearButton && !readOnly}
      readOnly={readOnly}
      dropDownOptions={DROP_DOWN_OPTIONS}
      contentRender={renderList}
      onValueChanged={handleValueChanged}
      onOpened={handleOpened}
      {...rest}
    />
  );
}
